/**
 * Stale-chunk recovery banner.
 *
 * Vite emits a `vite:preloadError` event on `window` whenever a dynamic
 * import fails to fetch its chunk — typically because a deploy went out
 * mid-session, the user's open tab still references the old chunk hash,
 * and the new build no longer contains that filename. With Cloudflare's
 * SPA fallback turned OFF (see wrangler.jsonc), those requests now 404
 * cleanly instead of serving index.html with `Content-Type: text/html`,
 * which is what made the underlying problem so hard to spot — Chrome
 * surfaced it as "Failed to fetch dynamically imported module" and
 * Safari as "'text/html' is not a valid JavaScript MIME type".
 *
 * This app holds embedding results (and optionally a live-camera
 * snapshot) in React state that the user hasn't necessarily copied or
 * downloaded yet. Forcing an auto-reload would silently throw those
 * results away, so the banner is interactive: the user taps Reload when
 * they're ready.
 *
 * Plain DOM (not React) so it survives errors that happen before mount
 * or during a render that itself depends on the failed chunk.
 */

let installed = false;

export function installPreloadErrorBanner(): void {
  if (installed) return;
  installed = true;

  let banner: HTMLDivElement | null = null;

  const show = () => {
    if (banner) return;
    banner = document.createElement("div");
    banner.setAttribute("role", "alert");
    banner.style.cssText = [
      "position:fixed",
      "top:0",
      "left:0",
      "right:0",
      "z-index:2147483647",
      "padding:12px 16px",
      "background:#c8102e",
      "color:#fff",
      "font:600 14px/1.4 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
      "display:flex",
      "align-items:center",
      "justify-content:space-between",
      "gap:12px",
      "box-shadow:0 2px 8px rgba(0,0,0,0.2)",
    ].join(";");

    const text = document.createElement("span");
    text.textContent =
      "Inference Check was updated. Reload to continue (unsaved results will be lost).";
    text.style.flex = "1";

    const button = document.createElement("button");
    button.type = "button";
    button.textContent = "Reload";
    button.style.cssText = [
      "appearance:none",
      "border:1px solid #fff",
      "background:#fff",
      "color:#c8102e",
      "border-radius:6px",
      "padding:8px 14px",
      "font:inherit",
      "cursor:pointer",
      "min-height:36px",
    ].join(";");
    button.addEventListener("click", () => window.location.reload());

    banner.append(text, button);
    (document.body ?? document.documentElement).append(banner);
  };

  // Vite-emitted event for failed dynamic imports. Payload includes the
  // module URL, but we don't surface it — the user doesn't care which
  // chunk, only that the app needs to refresh.
  window.addEventListener("vite:preloadError", () => show());

  // Belt-and-braces: catch the bare promise rejection in case Vite's
  // event ever stops firing (it has changed shape across Vite majors).
  // The string match is intentionally loose — Chrome and Safari phrase
  // this differently, and we'd rather false-positive a banner than miss
  // the real failure.
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
          ? reason
          : "";
    if (
      /failed to fetch dynamically imported module/i.test(message) ||
      /is not a valid javascript mime type/i.test(message)
    ) {
      show();
    }
  });
}
